import './ReportDocumentPageSavings.scss';
import { HvacArea, HvacResults } from '@belimo-retrofit-portal/logic';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { EnergyEfficiencyLabelWrapper } from 'src/modules/common/components/EnergyEfficiencyLabelWrapper';
import { EnergyEfficiencyStack } from 'src/modules/common/components/EnergyEfficiencyStack';
import { SavingsValue } from 'src/modules/common/components/SavingsValue';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { CloudIcon } from 'src/modules/common/icons/CloudIcon';
import { CoolingIcon } from 'src/modules/common/icons/CoolingIcon';
import { FanIcon } from 'src/modules/common/icons/FanIcon';
import { HeatingIcon } from 'src/modules/common/icons/HeatingIcon';
import { LightningIcon } from 'src/modules/common/icons/LightningIcon';
import { OutputDocumentPage } from 'src/modules/output/views/OutputDocumentPage';
import { OutputPage } from 'src/modules/output/views/OutputPage';
import { OutputTitle } from 'src/modules/output/views/OutputTitle';
import { ReportFormData } from 'src/modules/report/types/ReportFormData';
import { ReportGenerated } from 'src/modules/report/types/ReportGenerated';
import { ReportDocumentSavingsTileCard } from 'src/modules/report/views/document/ReportDocumentSavingsTileCard';
import { Language } from 'src/types/common/Language';

type Props = {
  readonly language: Language;
  readonly document: ReportGenerated;
  readonly formData: ReportFormData;
  readonly results: HvacResults;
};

export const ReportDocumentPageSavings = ({
  language,
  document,
  formData,
  results,
}: Props): React.ReactElement => (
  <OutputDocumentPage
    id="report-savings"
    order={2_000}
    section="savings"
    visible={formData.savings.enabled}
  >
    <OutputPage language={language} showPageNumber={true}>
      <div className="bp-report-document-page-savings">
        <div className="bp-report-document-page-savings__header">
          <OutputTitle>
            <FormattedMessage id="report/document/savings/efficiency/header"/>
          </OutputTitle>
        </div>

        {(formData.savings.showOverallEfficiency || formData.savings.showPerAreaEfficiency) && (
          <div className="bp-report-document-page-savings__content">
            {formData.savings.showPotentialSavings && (
              <div className="bp-report-document-page-savings__savings">
                <div className="bp-report-document-page-savings__header">
                  <FormattedMessage id="report/document/savings/potential/header"/>
                </div>

                <div className="bp-report-document-page-savings__potential-savings">
                  <ReportDocumentSavingsTileCard
                    savingTitle={<FormattedMessage id="report/document/savings/potential/energyRelative/title"/>}
                    icon={<HeatingIcon/>}
                    amountTitle={<FormattedMessage id="report/document/savings/potential/energyRelative/total"/>}
                    amount={(
                      <SavingsValue
                        value={document.savings.annualSavings.energyRelative.total?.mul(100) ?? null}
                        precision={0}
                      />
                    )}
                    unit={<FormattedMessage id="unit/percent"/>}
                    small={true}
                  >
                    <div className="bp-report-document-page-savings__potential-saving-area">
                      <div className="bp-report-document-page-savings__potential-saving-name">
                        <FormattedMessage id="report/document/savings/potential/details/heating"/>
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-value">
                        <SavingsValue
                          value={document.savings.annualSavings.energyRelative.heating?.mul(100) ?? null}
                          precision={0}
                        />
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-unit">
                        <FormattedMessage id="unit/percent"/>
                      </div>
                    </div>

                    <div className="bp-report-document-page-savings__potential-saving-area">
                      <div className="bp-report-document-page-savings__potential-saving-name">
                        <FormattedMessage id="report/document/savings/potential/details/cooling"/>
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-value">
                        <SavingsValue
                          value={document.savings.annualSavings.energyRelative.cooling?.mul(100) ?? null}
                          precision={0}
                        />
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-unit">
                        <FormattedMessage id="unit/percent"/>
                      </div>
                    </div>

                    <div className="bp-report-document-page-savings__potential-saving-area">
                      <div className="bp-report-document-page-savings__potential-saving-name">
                        <FormattedMessage id="report/document/savings/potential/details/electricalAuxiliary"/>
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-value">
                        <SavingsValue
                          value={document.savings.annualSavings.energyRelative.electricity?.mul(100) ?? null}
                          precision={0}
                        />
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-unit">
                        <FormattedMessage id="unit/percent"/>
                      </div>
                    </div>
                  </ReportDocumentSavingsTileCard>

                  <ReportDocumentSavingsTileCard
                    savingTitle={(
                      <FormattedMessage
                        id="report/document/savings/potential/carbonEmission/title"
                        values={{ sub }}
                      />
                    )}
                    icon={<CloudIcon/>}
                    amountTitle={(
                      <FormattedMessage
                        id="report/document/savings/potential/carbonEmission/total"
                        values={{ sub }}
                      />
                    )}
                    amount={(
                      <SavingsValue
                        value={results.overall.savings.carbon.relative?.mul(100) ?? null}
                        precision={0}
                      />
                    )}
                    unit={<FormattedMessage id="unit/percent"/>}
                    small={true}
                  >
                    <div className="bp-report-document-page-savings__potential-saving-area">
                      <div className="bp-report-document-page-savings__potential-saving-name">
                        <FormattedMessage id="report/document/savings/potential/details/heating"/>
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-value">
                        <SavingsValue
                          value={results.perArea.heating.savings.carbon.relative?.mul(100) ?? null}
                          precision={0}
                        />
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-unit">
                        <FormattedMessage id="unit/percent"/>
                      </div>
                    </div>

                    <div className="bp-report-document-page-savings__potential-saving-area">
                      <div className="bp-report-document-page-savings__potential-saving-name">
                        <FormattedMessage id="report/document/savings/potential/details/cooling"/>
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-value">
                        <SavingsValue
                          value={results.perArea.cooling.savings.carbon.relative?.mul(100) ?? null}
                          precision={0}
                        />
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-unit">
                        <FormattedMessage id="unit/percent"/>
                      </div>
                    </div>

                    <div className="bp-report-document-page-savings__potential-saving-area">
                      <div className="bp-report-document-page-savings__potential-saving-name">
                        <FormattedMessage id="report/document/savings/potential/details/ventilation"/>
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-value">
                        <SavingsValue
                          value={results.perArea.ventilation.savings.carbon.relative?.mul(100) ?? null}
                          precision={0}
                        />
                      </div>

                      <div className="bp-report-document-page-savings__potential-saving-unit">
                        <FormattedMessage id="unit/percent"/>
                      </div>
                    </div>
                  </ReportDocumentSavingsTileCard>

                  <ReportDocumentSavingsTileCard
                    last={true}
                    savingTitle={
                      <FormattedMessage id="report/document/savings/potential/energyAbsolute/title"/>
                    }
                    icon={
                      <LightningIcon/>
                    }
                    amountTitle={<FormattedMessage id="report/document/savings/potential/energyAbsolute/total"/>}
                    amount={(
                      <SavingsValue
                        value={results.overall.savings.overall.absolute ?? null}
                        precision={0}
                      />
                    )}
                    unit={(
                      <span className="bp-energy-savings-unit">
                        <FormattedMessage id="unit/kilowatt_hour" values={{ sub, sup }}/>/
                        <FormattedMessage id="unit/square_meter" values={{ sub, sup }}/>
                      </span>
                    )}
                    small={true}
                  />
                </div>
              </div>
            )}

            <div className="bp-report-document-page-savings__efficiency">
              {formData.savings.showOverallEfficiency && (
                <div className="bp-report-document-page-savings__efficiency-column">
                  <div className="bp-report-document-page-savings__header">
                    <FormattedMessage id="report/document/savings/efficiency/overall"/>
                  </div>

                  <div
                    className="bp-report-document-page-savings__efficiency-stacks"
                  >
                    <div className="bp-report-document-page-savings__efficiency-stack">
                      <FormattedMessage id="report/document/savings/efficiency/actual"/>

                      <EnergyEfficiencyStack
                        value={document.savings.ratingOverall.actual}
                      />
                    </div>

                    <div className="bp-report-document-page-savings__efficiency-stack">
                      <FormattedMessage id="report/document/savings/efficiency/future"/>

                      <EnergyEfficiencyStack
                        value={document.savings.ratingOverall.future}
                      />
                    </div>
                  </div>
                </div>
              )}

              {formData.savings.showPerAreaEfficiency && (
                <div className="bp-report-document-page-savings__efficiency-column">
                  <div className="bp-report-document-page-savings__header">
                    <FormattedMessage id="report/document/savings/efficiency/perArea"/>
                  </div>

                  <div className="bp-report-document-page-savings__overall-efficiency-periods">
                    <div><FormattedMessage id="report/document/savings/efficiency/actual"/></div>
                    <div><FormattedMessage id="report/document/savings/efficiency/future"/></div>
                  </div>

                  <div className="bp-report-document-page-savings__overall-efficiency-rows">
                    <div className="bp-report-document-page-savings__overall-efficiency-row">
                      <div className="bp-report-document-page-savings__overall-efficiency-description">
                        <div className="bp-report-document-page-savings__overall-efficiency-icon">
                          <HeatingIcon/>
                        </div>

                        <FormattedMessage id="report/document/energy/area/heating"/>
                      </div>

                      <div className="bp-report-document-page-savings__overall-efficiency-result">
                        <EnergyEfficiencyLabelWrapper
                          value={document.savings.ratingPerArea[HvacArea.HEATING].actual}
                          size="sm"
                          fixed={true}
                          disabled={false}
                        />

                        <div className="bp-report-document-page-savings__overall-efficiency-icon">
                          <ArrowRightIcon/>
                        </div>

                        <EnergyEfficiencyLabelWrapper
                          value={document.savings.ratingPerArea[HvacArea.HEATING].future}
                          size="sm"
                          fixed={true}
                          disabled={false}
                        />
                      </div>
                    </div>

                    <div className="bp-report-document-page-savings__overall-efficiency-row">
                      <div className="bp-report-document-page-savings__overall-efficiency-description">
                        <div className="bp-report-document-page-savings__overall-efficiency-icon">
                          <CoolingIcon/>
                        </div>

                        <FormattedMessage id="report/document/energy/area/cooling"/>
                      </div>

                      <div className="bp-report-document-page-savings__overall-efficiency-result">
                        <EnergyEfficiencyLabelWrapper
                          value={document.savings.ratingPerArea[HvacArea.COOLING].actual}
                          size="sm"
                          fixed={true}
                          disabled={false}
                        />

                        <div className="bp-report-document-page-savings__overall-efficiency-icon">
                          <ArrowRightIcon/>
                        </div>

                        <EnergyEfficiencyLabelWrapper
                          value={document.savings.ratingPerArea[HvacArea.COOLING].future}
                          size="sm"
                          fixed={true}
                          disabled={false}
                        />
                      </div>
                    </div>

                    <div className="bp-report-document-page-savings__overall-efficiency-row">
                      <div className="bp-report-document-page-savings__overall-efficiency-description">
                        <div className="bp-report-document-page-savings__overall-efficiency-icon">
                          <FanIcon/>
                        </div>

                        <FormattedMessage id="report/document/energy/area/ventilation"/>
                      </div>

                      <div className="bp-report-document-page-savings__overall-efficiency-result">
                        <EnergyEfficiencyLabelWrapper
                          value={document.savings.ratingPerArea[HvacArea.VENTILATION].actual}
                          size="sm"
                          fixed={true}
                          disabled={false}
                        />

                        <div className="bp-report-document-page-savings__overall-efficiency-icon">
                          <ArrowRightIcon/>
                        </div>

                        <EnergyEfficiencyLabelWrapper
                          value={document.savings.ratingPerArea[HvacArea.VENTILATION].future}
                          size="sm"
                          fixed={true}
                          disabled={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {formData.savings.showPotentialSavings && (
                <div>
                  <div className="bp-report-document-page-savings__header">
                    <FormattedMessage id="report/document/savings/about/header"/>
                  </div>

                  <div className="bp-report-document-page-savings__about">
                    <FormattedMessage
                      id="report/document/savings/about/text"
                      values={{ a, b }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </OutputPage>
  </OutputDocumentPage>
);

const a = (children: React.ReactNode): React.ReactElement => <span>{children}</span>;
const b = (children: React.ReactNode): React.ReactElement => <b>{children}</b>;
